// Centralized site configuration — easy to update copy in one place.
export const SITE = {
  name: "The Human Work Project",
  shortName: "The Human Work Project",
  legacyOf: "Built on 8 years of Therapist Tribe",
  // Main marketing tagline (used in hero)
  tagline: "The real human experiences..",
  // Action-oriented brand sub-label (under wordmark / micro-copy)
  taglineShort: "Understand. Grow. Thrive.",
  // Used on course / track pages
  courseTagline: "Training · Practice · Supervision",
  pullQuote: "Before you help someone, you learn how to be with them.",
  domain: "thehumanproject.in",
  email: "thehumanproject26@gmail.com",
  phoneDisplay: "+91 97183 83805",
  whatsappNumber: "919718383805",
  whatsappMessage: "Hi! I'd like to know more about The Human Work Project.",
  socials: {
    instagram: "https://instagram.com/thehumanworkproject_india",
    linkedin: "https://www.linkedin.com/company/the-human-project",
  },
};

// Org-level numbers shown on the homepage
export const ORG_STATS = [
  { n: "8 yrs", l: "Built on Therapist Tribe legacy" },
  { n: "800+", l: "Students & early-career practitioners trained" },
  { n: "1,500+", l: "Lives impacted via pro-bono & outreach work" },
  { n: "2", l: "Tracks · Foundation & Advanced Practice" },
];

// Five program pillars / highlights
export const PILLARS = [
  {
    title: "Psychological Skills Development",
    text: "Build core helping skills — listening, reflection, communication — through experiential exercises and supervised peer practice.",
  },
  {
    title: "Co-Counselling & Guided Practice",
    text: "Move from theory to lived skill via co-counselling sessions and pro-bono client work, held with structured supervision.",
  },
  {
    title: "Supervision & Reflective Spaces",
    text: "Case discussions, supervision circles, and guided reflection led by experienced practitioners.",
  },
  {
    title: "Group Facilitation & Supportive Conversations",
    text: "Practice psychoeducation, emotional wellness sessions, and group facilitation in real community settings.",
  },
  {
    title: "Mentorship, Certification & Real-World Exposure",
    text: "Structured mentorship, exposure to underserved community settings, a completion certificate, and a letter of experience.",
  },
];

// Five guiding principles from the program doc
export const PHILOSOPHY = [
  "People are not problems to be solved.",
  "Listening is more important than advising.",
  "Behaviour is shaped by context.",
  "Supporting someone requires humility and awareness.",
  "Psychological work is relational, not transactional.",
];

// Short note shown subtly on course pages
export const IMPORTANT_NOTE =
  "This program does not position participants as therapists. All interactions are guided, supervised, and aligned with ethical boundaries appropriate to each participant's level of training.";

// -----------------------------------------------------------------------------
// COMMUNITY IMPACT (Pro-Bono / social contribution)
// -----------------------------------------------------------------------------
export const IMPACT_STATS = [
  { n: "1,500+", l: "Individuals supported through pro-bono therapy & outreach" },
  { n: "800+", l: "Students & practitioners trained and mentored" },
  { n: "20+", l: "NGO & community collaborations" },
  { n: "8 yrs", l: "Continuous community mental-health work" },
];

export const IMPACT_STATEMENT = {
  eyebrow: "Our contribution to society",
  title: "Mental health, made accessible.",
  paragraphs: [
    "The Human Work Project sits within a wider community commitment. Alongside training and supervision, we run pro-bono therapy, low-cost referrals, and community mental-health work — because access to good psychological support should never depend on who you know or what you can pay.",
    "Every cohort contributes to this ecosystem — trainees engage with community NGOs, supervised pro-bono clients, and outreach programs for children, adolescents, and young adults from under-resourced backgrounds. In return, our empanelled therapists offer a limited number of pro-bono slots each month.",
  ],
  pillars: [
    { title: "Free therapy", text: "Pro-bono sessions with empanelled therapists — reserved every month for those who need it." },
    { title: "NGO fieldwork & outreach", text: "Structured mental-health programs with community partners — group sessions, psychoeducation, and individual support." },
    { title: "Trainer pipeline", text: "We train and mentor psychology students and early-career practitioners so more good therapists exist in the world." },
    { title: "Supervision networks", text: "Ongoing case supervision and reflective spaces for alumni continuing pro-bono work after the program." },
  ],
};

// -----------------------------------------------------------------------------
// EMPANELLED THERAPISTS (for the Pro-Bono page)
// Each `bookingUrl` links to the therapist's Propract profile. Set to null to
// show a "Bookings opening soon" tile instead.
// -----------------------------------------------------------------------------
export const THERAPISTS = [
  {
    slug: "umang-chadha",
    name: "Umang Chadha",
    initials: "UC",
    title: "Counselling Psychologist (Under Training)",
    credentials: "M.A. Psychology (University of Delhi) · CBT-trained",
    modalities: ["CBT", "Client-Centred", "Supportive Counselling"],
    focus:
      "Anxiety, depression, self-esteem, relationships, communication skills, life transitions, and academic/career concerns.",
    languages: ["English", "Hindi"],
    location: "Delhi",
    photoUrl: "https://res.cloudinary.com/dbgr4gh6x/image/upload/v1789209019/propract/photos/veqhn5d40qhmetk7afwg.jpg",
    bookingUrl: "https://propract.com/p/umang",
  },
  {
    slug: "ritu-jain",
    name: "Ritu Jain",
    initials: "RJ",
    title: "Therapist",
    credentials: "Blend of academic, mentoring & corporate experience",
    modalities: ["Empathetic", "Non-Judgmental"],
    focus:
      "Stress, anxiety, relationships, confidence and self-awareness, healthier coping — through supportive counselling.",
    languages: ["English", "Hindi"],
    location: "India",
    photoUrl: "https://lh3.googleusercontent.com/a/ACg8ocLx2cyYwIvBlmJeXeEGwUiSmjUU_5qVvSK8gkCXi9xlrXWw-_QI=s96-c",
    bookingUrl: "https://propract.com/p/ritu-jain-1195ca",
  },
  {
    slug: "sara-javaid",
    name: "Sara Javaid",
    initials: "SJ",
    title: "Counselling Psychologist",
    credentials: "M.A. Clinical Psychology · B.Sc. (Hons.) Neuropsychology & Neuroscience",
    modalities: ["CBT", "DBT", "Trauma-Informed"],
    focus:
      "Individual counselling with a background in neuroscience and neuropsychology. Career counselling, life coaching, health & wellness, emotional regulation, and psychoeducation for children, adolescents & adults.",
    languages: ["English", "Hindi", "Urdu"],
    location: "Srinagar, J&K",
    photoUrl: "https://res.cloudinary.com/dbgr4gh6x/image/upload/v1789372576/propract/photos/py3jicgd2holmferxueu.jpg",
    bookingUrl: "https://propract.com/p/sara-javaid",
  },
  {
    slug: "mithila-sunilkumar",
    name: "Mithila Sunilkumar",
    initials: "MS",
    title: "Counselling Psychologist",
    credentials: "M.A. Applied Psychology (TISS) · B.Sc. Psychology",
    modalities: ["Person-Centred", "Acceptance-based"],
    focus:
      "Adults (18+) navigating identity, life transitions, self-destructive patterns, burnout, low moods, relationships, and academic/work stress.",
    languages: ["English", "Malayalam", "Hindi"],
    location: "Kerala",
    photoUrl: "https://res.cloudinary.com/dbgr4gh6x/image/upload/v1789198178/propract/photos/d4a9sohefpt7zrcmba4c.jpg",
    bookingUrl: "https://propract.com/p/mithila-sunilkumar",
  },
  {
    slug: "kriti-sharma",
    name: "Kriti Sharma",
    initials: "KS",
    title: "Psychotherapist",
    credentials: "M.A. Clinical Psychology · APA International Affiliate",
    modalities: ["CBT", "DBT", "Trauma-informed", "Crisis Intervention"],
    focus:
      "Adults, adolescents & older adults. Anxiety, trauma, career transitions, ADHD/autism assessments, depression, addiction & coping.",
    languages: ["English", "Hindi"],
    location: "Delhi",
    photoUrl: "https://res.cloudinary.com/dbgr4gh6x/image/upload/v1789294798/propract/photos/jcy01sq2to0rghrytw9a.jpg",
    bookingUrl: "https://propract.com/p/kritipsychotherpist",
  },
];

// -----------------------------------------------------------------------------
// TEAM (Astha leads · Ruhika & Aditi on team)
// -----------------------------------------------------------------------------
export const FOUNDERS = [
  {
    slug: "astha-ahluwalia",
    name: "Astha Ahluwalia",
    role: "Founder & Lead Psychotherapist · Psychotherapist | Educator | Supervisor",
    experience: "17+ years",
    intro:
      "Astha Ahluwalia is a psychotherapist, educator, and supervisor with over 17 years of experience in counselling, psychotherapy, mental health training, and professional development. Her work spans therapeutic practice, therapist education, supervision, and community mental health initiatives across India and internationally.",
    bio: [
      "As the former co-founder of Reboot Wellness (2018–2026) and the creator of Therapist Tribe, Astha has trained and mentored more than 800 psychology students, counsellors, and early-career therapists. Her teaching philosophy is rooted in the belief that becoming an effective therapist requires more than learning techniques; it requires developing the ability to think critically, reflect deeply, understand human behaviour, and build authentic therapeutic relationships.",
      "She is the creator and lead facilitator of *Thinking Like a Therapist*, a signature training module that helps therapists strengthen core clinical competencies including therapeutic presence, listening, formulation, conceptualization, case understanding, ethical decision-making, and the thoughtful use of interventions. The programme integrates insights from contemporary psychotherapy, real clinical practice, supervision, and reflective learning.",
      "Astha's training style is experiential, practical, and deeply reflective. She creates learning spaces that encourage curiosity, self-awareness, skill development, and professional confidence, helping participants bridge the gap between academic knowledge and the realities of therapeutic work.",
      "As a practitioner and educator she follows an eclectic approach which integrates and departs in her trainings. Her therapeutic inclination includes neo-cognitive behavioural therapies like DBT and ACT, along with a trauma-informed lens, built on the foundation of client-centred therapy. Her practice is relational and depth-oriented while holding the importance of therapy in its active form.",
      "In addition to training and supervision, Astha continues to work extensively with individuals, couples, and families, bringing current clinical experience into every learning space she facilitates.",
    ],
    highlights: [
      "Creator of Therapist Tribe",
      "Former Co-Founder, Reboot Wellness (2018–2026)",
      "Creator & lead facilitator of 'Thinking Like a Therapist'",
      "800+ psychology students, counsellors & early-career therapists trained",
    ],
    philosophy:
      "Becoming an effective therapist requires more than learning techniques — it requires the ability to think critically, reflect deeply, understand human behaviour, and build authentic therapeutic relationships.",
    initials: "AA",
  },
  {
    slug: "ruhika-gambhir",
    name: "Ruhika Gambhir",
    role: "Team Psychologist · Counselling Psychologist",
    experience: "9+ years",
    intro:
      "Ruhika brings extensive experience across clinical practice, corporate wellbeing, student engagement, and community mental-health programs across India, the USA, and the UAE.",
    bio: [
      "Ruhika brings extensive experience across clinical practice, corporate wellbeing, student engagement, and community mental-health programs across India, the USA, and the UAE.",
      "Her work spans therapy, training, and mental-health support for diverse populations — building bridges between clinical care and everyday wellbeing.",
    ],
    highlights: [
      "Worked with ComPsych, Optum, Santulan EAPs",
      "Supported employees at Orange, Samsung, NatWest, Uber, DuPont, Cargill, Amex",
      "Author & presenter: 'Adolescents with Broken Heart'",
      "Cross-disciplinary collaborations with psychiatrists & healthcare professionals",
    ],
    philosophy:
      "Building bridges between clinical care and everyday wellbeing — across cultures and contexts.",
    initials: "RG",
  },
  {
    slug: "aditi-mukund",
    name: "Aditi Mukund",
    role: "Team Clinical Psychologist · M.Phil. Clinical Psychology (RCI-Licensed)",
    experience: "Clinical Psychologist",
    intro:
      "Aditi Mukund is an RCI-licensed Clinical Psychologist with experience across hospital, rehabilitation, educational, and private practice settings. Her work is primarily informed by CBT, while also drawing from EFT and DBT-based approaches. She has a particular interest in making psychology feel less intimidating and more practical — whether in the therapy room or in training spaces.",
    bio: [
      "Aditi Mukund is an RCI-licensed Clinical Psychologist with experience across hospital, rehabilitation, educational, and private practice settings. Her work is primarily informed by CBT, while also drawing from EFT and DBT-based approaches.",
      "She has a particular interest in making psychology feel less intimidating and more practical — whether in the therapy room or in training spaces.",
      "Alongside her clinical work, Aditi has served as a trainer and educator for psychology students, trainees, and early-career mental health professionals through Therapist Tribe. Her sessions focus on bridging the gap between theory and practice, encouraging participants to think critically, stay curious, and develop confidence in their own therapeutic style.",
      "Aditi's training style is interactive, reflective, and rooted in real-world clinical experiences. She enjoys creating learning spaces where participants feel comfortable asking questions, exploring uncertainty, and connecting psychological theory with the realities of practice.",
    ],
    highlights: [
      "M.Phil. Clinical Psychology",
      "RCI-Licensed Clinical Psychologist",
      "Hospital · Rehabilitation · Educational · Private practice",
      "Trainer with Therapist Tribe — bridging theory and practice",
    ],
    workshops: [
      {
        title: "CBT as a Way of Thinking",
        text: "A three-day training exploring CBT beyond techniques and worksheets — focusing on CBT as a way of understanding clients, making sense of their experiences, and approaching therapy with greater clarity and flexibility.",
      },
      {
        title: "Crisis Intervention in Mental Health Settings",
        text: "A workshop covering crisis assessment, risk evaluation, immediate intervention strategies, and practical considerations when supporting individuals experiencing acute psychological distress.",
      },
      {
        title: "Coping with Grief (co-facilitated)",
        text: "A session focused on understanding the many ways grief can present, factors that influence the grieving process, and how mental health professionals can support clients navigating loss.",
      },
      {
        title: "Navigating Clinical Practice — Practical Challenges & Therapist Well-being",
        text: "An interactive workshop exploring the realities of clinical work — boundaries, burnout, self-doubt, ethical dilemmas, and ways therapists can care for themselves while caring for others.",
      },
    ],
    philosophy:
      "Making psychology feel less intimidating and more practical — in the therapy room and in training spaces.",
    initials: "AM",
  },
];

// -----------------------------------------------------------------------------
// FOUNDATION TRACK (revised per program doc)
// -----------------------------------------------------------------------------
export const FOUNDATION_TRACK = {
  slug: "foundation-track",
  name: "Foundation Track",
  fullName: "Foundation Track — Understanding People & Emotions",
  tagline: "Real Communities. Real Skills. Real Impact.",
  shortPitch:
    "A grounded introduction to understanding people, emotions, and everyday psychological experiences — for students and individuals new to the field.",
  description:
    "The Foundation Track is designed to build a grounded understanding of people, emotions, and everyday psychological experiences. It introduces participants to the basics of psychological thinking, communication, and emotional awareness, while offering guided exposure to real-world mental health contexts. The emphasis is on learning how to observe, listen, and respond — rather than advise or fix.",
  startDate: "1 December 2026",
  endDate: "1 January 2027",
  durationLabel: "1 month",
  totalHoursLabel: "80+ hours",
  totalHoursNote: "Training, field experience and delivery under supervision",
  format: "Hybrid — Online training + Offline NGO fieldwork",
  feeINR: 12000,
  feeNote: "Limited seats · Selection-based",
  commitment: "~15–20 hours/week",
  audience: [
    "Grade 11 & 12 students studying psychology",
    "Undergraduate psychology students",
    "Postgrad students — Psychology & Social Work",
    "Individuals from non-psychology backgrounds (interview selection)",
  ],
  modules: [
    {
      n: 1,
      title: "Foundations of Psychological Thinking",
      sessions: 1,
      points: [
        "Understanding behaviour beyond assumptions",
        "Observing vs interpreting",
        "Awareness of biases and judgment",
      ],
    },
    {
      n: 2,
      title: "Basic Counselling & Communication Skills",
      sessions: 2,
      points: [
        "Active listening",
        "Reflection and paraphrasing",
        "Asking meaningful questions",
        "Holding space vs giving advice",
      ],
    },
    {
      n: 3,
      title: "Self & Identity + Emotional Regulation & Coping",
      sessions: 2,
      points: [
        "Self-awareness and personal narratives",
        "Self image and self-worth",
        "Identifying personal patterns and triggers",
        "Understanding emotional experiences",
        "Regulation vs suppression",
        "Building adaptive coping tools",
        "Introduction to emotional intelligence",
      ],
    },
    {
      n: 4,
      title: "Relationship Dynamics",
      sessions: 1,
      points: [
        "Communication patterns",
        "Maintaining healthy relationships",
        "Introduction to boundaries and safety in relationships",
      ],
    },
    {
      n: 5,
      title: "Psychological First Aid & Distress Support",
      sessions: 1,
      points: [
        "Responding to emotional overwhelm",
        "Supporting someone in distress",
        "Basic crisis sensitivity (non-clinical)",
      ],
    },
    {
      n: 6,
      title: "Community Mental Health & Peer Support",
      sessions: 1,
      points: [
        "Role of community in mental health",
        "Supporting others in everyday contexts",
        "Introduction to ethical responsibility in helping",
      ],
    },
    {
      n: 7,
      title: "Human-Centred Field Practice",
      sessions: 2,
      points: [
        "Building trust, rapport & psychological safety",
        "Communication skills, active listening & supportive conversations",
        "Conducting group work & activities",
        "Basics of one-to-one supportive interaction",
        "Working with boundaries, confidentiality & ethical practice",
        "Handling difficult situations & emotional responses in the field",
      ],
    },
  ],
  methodology: [
    "Case studies and real-life scenarios",
    "Co-counselling practice (guided peer work)",
    "Field immersion with community partners",
    "Structured reflection exercises",
    "Supervised group discussions",
  ],
  outcomes: [
    "Completion Certificate",
    "Letter of Experience",
    "Foundational skills in understanding emotions and communication",
    "Exposure to community mental health settings",
  ],
  selectionNote:
    "All practice components are introduced progressively. Participants engage in real-world exposure only after foundational readiness is established. Selection is based on interest, intent, and readiness to engage.",
};

// -----------------------------------------------------------------------------
// ADVANCED PRACTICE TRACK (revised per program doc)
// -----------------------------------------------------------------------------
export const PRACTICE_TRACK = {
  slug: "practice-track",
  name: "Advanced Practice Track",
  fullName: "Advanced Practice Track — Working with People in Real Settings",
  tagline: "From classroom to clinic — held, supervised, real.",
  shortPitch:
    "For postgraduate psychology students and early-career therapists ready to move from theoretical learning into applied, supervised, real-world practice.",
  description:
    "The Advanced Practice Track is designed for individuals with a background in psychology who are looking to move from theoretical learning into applied, real-world practice. The focus is on developing therapeutic thinking, advanced skills, and professional identity, while engaging in supervised interactions and field-based work.",
  startDate: "1 December 2026",
  endDate: "28 February 2027",
  durationLabel: "3 months",
  totalHoursLabel: "75+ hours",
  totalHoursNote: "Training, field experience and client work under supervision",
  format: "Hybrid — Online training + Optional NCR field work",
  feeINR: 15000,
  feeNote: "Limited seats · Selection-based",
  commitment: "~15–20 hours/week",
  audience: [
    "Postgraduate psychology students",
    "Early-career therapists & counsellors",
  ],
  components: [
    { kind: "online", label: "Training Sessions (Online)", detail: "26 sessions · 52 hours" },
    { kind: "offline", label: "Field Work (Offline · NCR)", detail: "Optional · community / OB · 8-hour delivery module" },
    { kind: "online", label: "Co-Counselling", detail: "8 hours of supervised peer practice" },
    { kind: "online", label: "Online Pro-Bono Therapy", detail: "2 – 3 clients (post-readiness)" },
    { kind: "online", label: "Supervision & Reflection (Online)", detail: "5 sessions · 10 hours" },
    { kind: "online", label: "Culmination & Presentation", detail: "1 session · 2 hours" },
  ],
  modules: [
    {
      n: 1,
      title: "Thinking Like a Therapist — Advanced Therapeutic Skills",
      sessions: 6,
      points: [
        "Deep listening and emotional attunement",
        "Working with silence and affect",
        "Moving beyond surface-level responses",
        "Case understanding & formulation",
        "Understanding client narratives",
        "Identifying patterns and themes",
        "Meaning-making in context",
        "Consent and other forms",
      ],
    },
    {
      n: 2,
      title: "Therapy Modalities (Applied Understanding)",
      sessions: 9,
      points: [
        "Cognitive Behaviour Therapy — Basic & Mid Level (3 sessions)",
        "Dialectical Behaviour Therapy — Basic & Mid (4 sessions)",
        "Trauma-informed approaches to counselling (2 sessions)",
      ],
    },
    {
      n: 3,
      title: "Self as a Therapist",
      sessions: 1,
      points: [
        "Personal triggers and biases",
        "Emotional responses in sessions",
        "Boundaries and presence",
      ],
    },
    {
      n: 4,
      title: "Ethics & Responsibility in Practice",
      sessions: 1,
      points: [
        "Scope of practice",
        "Ethical dilemmas in real settings",
        "Professional responsibility",
      ],
    },
    {
      n: 5,
      title: "Working with Relationships",
      sessions: 4,
      points: [
        "Interpersonal dynamics",
        "Introduction to couple and relational frameworks",
        "Working with young adults (attachment) + navigating romantic relationships",
        "Parent–child relationship",
      ],
    },
    {
      n: 6,
      title: "Applied Therapeutic Lens",
      sessions: 2,
      points: [
        "Addiction and coping",
        "Crisis intervention",
      ],
    },
    {
      n: 7,
      title: "Community Mental Health + Organisational Behaviour",
      sessions: 4,
      points: [
        "Community psychology & mental health in community spaces",
        "Psychological impact of poverty, instability, trauma & social inequality",
        "Working with children, adolescents & young adults from under-resourced backgrounds",
        "Trauma-informed & strength-based approaches to support",
        "Recognising signs that require further support or intervention",
        "Referral pathways, case flagging & role of supervision",
        "Mental health awareness, resilience & psychoeducation in communities",
        "Organisational Behaviour (2 sessions)",
      ],
    },
    {
      n: 8,
      title: "Human-Centred Field Practice",
      sessions: 1,
      points: [
        "Building trust, rapport & psychological safety",
        "Communication, active listening & supportive conversations",
        "Conducting group work & psychoeducational activities",
        "Facilitating emotional expression & safe group spaces",
        "Creative & low-resource interventions for community settings",
        "Working with boundaries, confidentiality & ethical practice",
        "Handling difficult situations & emotional responses in the field",
        "Self-awareness, reflection & emotional preparedness as a helper",
        "Team coordination, documentation & working within support structures",
        "Role plays, case discussions & practical community-based exercises",
      ],
    },
  ],
  fieldWork: {
    title: "Field Practice & Client Interaction",
    points: [
      "NGO-based interventions — group sessions",
      "Structured individual counselling and therapy sessions",
      "Supervised client interaction (post-training readiness only)",
    ],
  },
  supervision: {
    title: "Supervision & Reflective Practice",
    points: [
      "Case discussions",
      "Feedback from practicing therapists",
      "Emotional processing and integration",
      "4 group supervisions",
    ],
  },
  methodology: [
    "Advanced co-counselling practice",
    "Case discussions and formulation exercises",
    "Supervision circles with practicing therapists",
    "Field-based interventions",
    "Guided reflection and feedback",
  ],
  outcomes: [
    "Completion Certificate",
    "Letter of Experience",
    "Supervised practical exposure",
    "Experience in real-world mental health settings",
  ],
  progression:
    "Participants who demonstrate readiness, emotional maturity, ethical understanding, and foundational counselling competencies during the program may be offered the opportunity to continue into supervised one-to-one online client work with adults. Those who consistently show strong aptitude and commitment may be considered for referrals, collaborations, or relevant opportunities within allied mental health spaces.",
  selectionNote:
    "All practice components are introduced progressively. Real-world exposure begins only after foundational readiness is established. Selection is based on interest, intent, and readiness to engage.",
};

export const TRACKS = [FOUNDATION_TRACK, PRACTICE_TRACK];

// Field work context (shared)
export const FIELD_WORK_CONTEXT = {
  title: "Field Work",
  intro:
    "Participants engage with community partners (NGOs) and work with children and adolescents (ages 8–18), young adults, and teachers.",
  points: [
    "Designing age-appropriate mental health sessions",
    "Delivering structured group activities",
    "Engaging in supportive conversations within ethical boundaries",
    "Reflecting on real-world experiences",
  ],
};

// -----------------------------------------------------------------------------
// REAL TESTIMONIALS (Raga kept + Meena & Sakshi added)
// -----------------------------------------------------------------------------
export const TESTIMONIALS = [
  {
    quote:
      "The intention behind the program, the cohort size, the space to absorb, and the caring teachers — it genuinely felt like the IIT of psychotherapists. I came in unsure and left grounded.",
    name: "Sanskar Sinha",
    role: "Counselling Psychologist, Bhopal",
    batch: "Jan 2026",
  },
  {
    quote:
      "A truly transformational journey. The training surfaced the nuances of real-time client distress that no textbook captures. Astha Ahluwalia is a dedicated, knowledgeable, and accessible trainer and supervisor.",
    name: "Huma Bisnagri",
    role: "Counselling Psychologist, UAE",
    batch: "Jun 2024",
  },
  {
    quote:
      "All modules were taught in a detailed, practical, and easy-to-understand manner. The DBT teaching was engaging and full of practical learning. The best part was the pro-bono sessions with supervision — that's where confidence really comes from.",
    name: "Meena Saboo",
    role: "Counselling Psychologist, Mumbai",
    batch: "Feb 2025",
  },
  {
    quote:
      "I so wish I could enroll for the program earlier — it was all paisa vasool. I can never get tired of hearing and revisiting the sessions. I hope to become an amazing therapist Astha ma'am is someday.",
    name: "Sakshi Kothari",
    role: "Counselling Psychologist, Jaipur",
    batch: "Jan 2025",
  },
  {
    quote:
      "I was struggling with the transition from student to practitioner. This space gave me room to learn, explore, practice, and grow — with steady support and encouragement throughout.",
    name: "Raga Viveka",
    role: "Counselling Psychologist",
    batch: "2023",
  },
];

// -----------------------------------------------------------------------------
// FAQs
// -----------------------------------------------------------------------------
export const FAQS_SITE = [
  {
    q: "What is The Human Work Project?",
    a: "The Human Work Project is a structured, practice-oriented psychology and mental health program designed to bridge the gap between theoretical understanding and real-world human engagement — built on 8 years of Therapist Tribe.",
  },
  {
    q: "Who runs the program?",
    a: "The program is designed and facilitated by practising therapists and clinical psychologists, led by Astha Ahluwalia, with Ruhika Gambhir, Aditi Mukund and a team of experienced practitioners and NGO partners.",
  },
  {
    q: "Are your programs therapy?",
    a: "No. Our programs are education, training, and supervised practice. They are not a substitute for personal therapy. This program does not position participants as therapists — all interactions are guided, supervised, and aligned with ethical boundaries appropriate to each participant's level of training.",
  },
  {
    q: "Which track is right for me?",
    a: "The Foundation Track is for Grade 11–12 students, undergraduates, postgrad psychology/social work students, and individuals from non-psychology backgrounds (interview-based). The Advanced Practice Track is for postgraduate psychology students and early-career therapists/counsellors ready for applied client work and supervision.",
  },
  {
    q: "Where does the field work happen?",
    a: "Field work for the Foundation Track happens in NCR with our community NGO partners. For the Advanced Practice Track, field work is optional and offered in NCR. Online sessions are open to learners from anywhere.",
  },
  {
    q: "How are participants selected?",
    a: "Seats are limited per cohort. Selection is based on interest, intent, and readiness to engage. Participants may be asked to complete a short application form. Once you express interest, we'll get on a short call to make sure the program is the right fit for you.",
  },
];

export const FAQS_FOUNDATION = [
  {
    q: "Do I need a psychology background to join the Foundation Track?",
    a: "No prior background required. The track is open to Grade 11–12 students, undergraduates, postgrad students in psychology/social work, and individuals from non-psychology backgrounds (interview selection).",
  },
  {
    q: "What is the duration & time commitment?",
    a: "The Foundation Track runs from 1 December 2026 to 1 January 2027 — about 1 month, with 80+ hours of training, field experience and supervised delivery. Expect around 15–20 hours per week.",
  },
  {
    q: "What will I receive at the end?",
    a: "A Completion Certificate, a Letter of Experience, foundational skills in understanding emotions and communication, and exposure to community mental health settings.",
  },
  {
    q: "What does 'practice components introduced progressively' mean?",
    a: "We never put learners into client-facing situations cold. You'll first build readiness through training and structured exercises. Field exposure begins only after that foundation is established, and is always held with supervision.",
  },
  {
    q: "What is the fee? Are there discounts?",
    a: "The Foundation Track fee is INR 12,000. Group discounts may be available — for refund or rescheduling, please reach out via WhatsApp or the contact form and we'll handle each request with care.",
  },
];

export const FAQS_PRACTICE = [
  {
    q: "Who is the Advanced Practice Track for?",
    a: "Postgraduate psychology students and early-career therapists or counsellors who are ready to move from coursework into applied, supervised practice.",
  },
  {
    q: "How is it different from the Foundation Track?",
    a: "The Advanced Practice Track assumes a psychology background and goes deeper into applied therapy modalities (CBT, DBT, trauma-informed), case formulation, ethics & self of the therapist, and supervised client interaction. It also runs over 3 months (1 December 2026 – 28 February 2027).",
  },
  {
    q: "Will I do real client work?",
    a: "Yes — participants who demonstrate readiness during training may be offered supervised one-to-one online client work with adults, along with 2–3 pro-bono clients and 8 hours of co-counselling, all held with structured supervision.",
  },
  {
    q: "Is supervision included throughout?",
    a: "Yes. Supervision and reflective spaces are central — 5 supervision sessions (10 hours) plus 4 group supervisions, with feedback from practising therapists and clinical psychologists.",
  },
  {
    q: "What is the fee?",
    a: "The Advanced Practice Track fee is INR 15,000. Apply via the booking widget on this page, or message us first via WhatsApp / contact form.",
  },
  {
    q: "How are participants selected?",
    a: "Limited seats per cohort. Selection is based on interest, intent, and readiness to engage. We aim for cohorts that can learn alongside each other comfortably.",
  },
];
